<template>
  <div class="w-12 bg-gray-800 flex flex-col items-center border-r border-gray-700 shrink-0 overflow-hidden">

    <!-- ── Repo icons (top) ──────────────────────────────────────────── -->
    <div class="flex flex-col items-center gap-1 pt-2 w-full px-1">

      <!-- One icon per cloned user repository, up to 5 -->
      <button
        v-for="(repo, idx) in clonedRepos"
        :key="repo.id"
        :title="repo.label"
        class="relative w-10 h-10 rounded flex items-center justify-center text-[11px] font-bold transition-colors leading-none"
        :class="[
          REPO_COLORS[idx % REPO_COLORS.length],
          String(repo.id) === repoStore.activeRepo?.id
            ? 'opacity-100 ring-1 ring-white/40'
            : 'opacity-60 hover:opacity-90',
        ]"
        @click="activateUserRepo(repo)"
      >
        {{ initials(repo.label) }}
        <!-- Left-edge active indicator (VS Code style) -->
        <span
          v-if="String(repo.id) === repoStore.activeRepo?.id"
          class="absolute left-0 top-2 bottom-2 w-0.5 bg-white rounded-r"
        />
      </button>

    </div>

    <!-- ── Separator ─────────────────────────────────────────────────── -->
    <div class="w-7 border-t border-gray-700 my-2 shrink-0" />

    <!-- ── Tool icons ────────────────────────────────────────────────── -->
    <div class="flex flex-col items-center gap-1 w-full px-1">
      <button
        v-for="item in TOOLS"
        :key="item.id"
        :title="item.label"
        class="relative w-10 h-10 flex items-center justify-center rounded transition-colors"
        :class="activePanel === item.id
          ? 'text-white bg-gray-700'
          : 'text-gray-400 hover:text-white hover:bg-gray-700/60'"
        @click="$emit('activate', item.id)"
      >
        <span class="text-lg leading-none select-none">{{ item.icon }}</span>
        <!-- Left-edge active indicator -->
        <span
          v-if="activePanel === item.id"
          class="absolute left-0 top-2 bottom-2 w-0.5 bg-white rounded-r"
        />
      </button>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRepoStore } from '@/stores/repoStore'
import type { UserRepository } from '@/stores/repoStore'

defineProps<{ activePanel: string }>()
const emit = defineEmits<{ (e: 'activate', panel: string): void }>()

const repoStore = useRepoStore()

const clonedRepos = computed(() =>
  repoStore.userRepos.filter(r => r.cloneStatus === 'ready').slice(0, 5),
)

// ── Tool definitions ─────────────────────────────────────────────────────────

const TOOLS = [
  { id: 'explorer',  icon: '📄', label: 'File Explorer' },
  { id: 'git',       icon: '⎇',  label: 'Source Control' },
  { id: 'claude',    icon: '✦',  label: 'Claude AI' },
  { id: 'nas-repos', icon: '🗄',  label: 'Repositories' },
  { id: 'terminal',  icon: '>_', label: 'Terminal  Ctrl+`' },
] as const

// ── Repo icon colours ────────────────────────────────────────────────────────
// Each repository gets a stable colour derived from its slot index.

const REPO_COLORS = [
  'bg-blue-600 text-white',
  'bg-purple-600 text-white',
  'bg-teal-600 text-white',
  'bg-amber-600 text-white',
  'bg-rose-600 text-white',
]

/** Two-letter initials from a kebab/snake/space separated name. */
function initials(name: string): string {
  const parts = name.split(/[-_.\s]+/).filter(Boolean)
  if (parts.length === 1) return name.slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function activateUserRepo(repo: UserRepository): void {
  repoStore.activateRepo(repo)
  emit('activate', 'explorer')
}
</script>
